import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Trash2, Building2, UsersRound, Shield, Lock, Monitor, RefreshCw } from "lucide-react";
import { PageHeader, PageBody, Field } from "@/components/PageChrome";
import { Input, Select, Button } from "@/components/controls";
import { T } from "@/lib/testIds";
import { usePerms } from "@/lib/auth";
import PaginationBar from "@/components/PaginationBar";
import useClientPagination from "@/hooks/useClientPagination";

function AccountChips({ ids, max = 4 }) {
  if (!ids || !ids.length) {
    return <span className="inline-flex items-center gap-1 h-5 px-1.5 text-[10px] font-bold uppercase tracking-wider rounded-sm bg-slate-100 text-slate-500 border border-slate-200">
      <Lock size={9} /> None
    </span>;
  }
  const shown = ids.slice(0, max);
  const more = ids.length - shown.length;
  return (
    <span className="inline-flex flex-wrap gap-1 items-center">
      {shown.map((a) => (
        <span key={a} data-testid={`user-account-chip-${a}`} className="inline-flex items-center h-5 px-1.5 text-[10px] font-bold rounded-sm bg-indigo-50 text-indigo-800 border border-indigo-200 font-mono">
          {a}
        </span>
      ))}
      {more > 0 && (
        <span className="inline-flex items-center h-5 px-1.5 text-[10px] font-semibold rounded-sm bg-slate-100 text-slate-600 border border-slate-200" title={ids.slice(max).join(", ")}>
          +{more} more
        </span>
      )}
    </span>
  );
}

function TeamChips({ names }) {
  if (!names || !names.length) return <span className="text-slate-400">—</span>;
  return (
    <span className="inline-flex flex-wrap gap-1">
      {names.map((n) => (
        <span key={n} className="inline-flex items-center h-5 px-1.5 text-[10px] font-semibold rounded-sm bg-slate-100 text-slate-700 border border-slate-200">
          {n}
        </span>
      ))}
    </span>
  );
}

function RoleBadge({ role }) {
  const isAdmin = role === "admin";
  return (
    <span className={`inline-flex items-center gap-0.5 h-5 px-1.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border ${isAdmin ? "bg-amber-50 text-amber-900 border-amber-300" : "bg-slate-100 text-slate-700 border-slate-200"}`}>
      <Shield size={9} /> {role || "user"}
    </span>
  );
}

export default function Users() {
  const [rows, setRows] = useState([]);
  const [teams, setTeams] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [modules, setModules] = useState([]);
  // Mar 1 2026 — Warehouses used for the per-user Warehouse Allocation section.
  // Falls back to a distinct-warehouse-name list derived from `accounts` if
  // the canonical Warehouse table is empty.
  const [warehouses, setWarehouses] = useState([]);
  const [editing, setEditing] = useState(null);
  const { canView, canEdit } = usePerms();
  const editable = canEdit("users");
  const COLS = [
    { k: "name", label: "Name", field: "name", render: (u) => <td className="font-medium">{u.name}</td> },
    { k: "email", label: "Email", field: "email", render: (u) => <td className="font-mono text-[11.5px] text-slate-600">{u.email}</td> },
    { k: "role", label: "Role", field: "role", render: (u) => <td><RoleBadge role={u.role} /></td> },
    { k: "teams", label: "Teams", field: "teams", render: (u) => <td><TeamChips names={u.teams || []} /></td> },
    { k: "account_ids", label: "Account Allocation", field: "account_ids", render: (u) => (
      <td>
        <div className="flex items-center gap-1.5">
          <AccountChips ids={u.account_ids || []} />
          {u.role !== "admin" && (u.account_ids || []).length > 0 && (
            <span className="text-[10.5px] text-slate-500 font-mono">({(u.account_ids || []).length})</span>
          )}
          {u.role === "admin" && (
            <span className="text-[10px] text-slate-400 italic">(sees all)</span>
          )}
        </div>
      </td>
    ) },
    { k: "created_at", label: "Created", field: null, render: (u) => <td className="font-mono text-[11px] text-slate-500">{u.created_at?.slice(0, 10)}</td> },
  ];
  const visibleCols = COLS.filter((c) => !c.field || canView("users", c.field));
  const pag = useClientPagination(rows, "users");

  const load = async () => {
    try {
      const [u, t, a, m, w] = await Promise.all([
        api.get("/users"),
        api.get("/teams"),
        api.get("/accounts"),
        api.get("/teams/meta/modules"),
        // Mar 1 2026 — canonical Warehouse table for the per-user allowlist.
        api.get("/warehouse"),
      ]);
      setRows(u.data); setTeams(t.data); setAccounts(a.data);
      setModules(m.data?.modules || []);
      const whs = Array.isArray(w.data) ? w.data : (w.data?.items || []);
      setWarehouses(whs);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    // Guardrails: creating a non-admin user without any team means they will
    // land on an empty Dashboard with no perms — which admins mistake for a
    // broken login. Force explicit team assignment so this never happens.
    if (!editing.id && !editing.password) {
      toast.error("Password is required to create a user");
      return;
    }
    if (editing.role !== "admin" && (!editing.teams || editing.teams.length === 0)) {
      toast.error("Please assign at least one Team to this user — otherwise they'll log in but see empty screens.");
      return;
    }
    try {
      if (editing.id) {
        await api.patch(`/users/${editing.id}`, editing);
      } else {
        await api.post("/users", editing);
      }
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async () => {
    if (!window.confirm("Delete user?")) return;
    try { await api.delete(`/users/${editing.id}`); toast.success("Deleted"); setEditing(null); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleArr = (key, val) => setEditing((s) => {
    const arr = new Set(s?.[key] || []);
    if (arr.has(val)) arr.delete(val); else arr.add(val);
    return { ...s, [key]: Array.from(arr) };
  });

  const editingAccountIds = editing?.account_ids || [];

  // ---- Active-sessions manager (Feb 2026) ----
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const loadSessions = async (uid) => {
    if (!uid) { setSessions([]); return; }
    setSessionsLoading(true);
    try {
      const { data } = await api.get("/auth/sessions", { params: { user_id: uid } });
      setSessions(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSessionsLoading(false); }
  };
  useEffect(() => { loadSessions(editing?.id); }, [editing?.id]);

  const revokeSession = async (sid) => {
    try {
      await api.delete(`/auth/sessions/${sid}`);
      toast.success("Session revoked");
      loadSessions(editing.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const revokeAll = async () => {
    if (!window.confirm("Revoke ALL active sessions for this user?")) return;
    try {
      const { data } = await api.delete("/auth/sessions", { params: { user_id: editing.id } });
      toast.success(`Revoked ${data.revoked_count} session(s)`);
      loadSessions(editing.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <>
      <PageHeader title="Users" subtitle="System accounts, team assignments & account allocation" testId={T.moduleTitle("users")} module="users">
        {editable && (
          <Button variant="primary" onClick={() => setEditing({ role: "user", teams: [], account_ids: [] })} testId={T.newBtn("users")}>
            <Plus size={13} /> New User
          </Button>
        )}
      </PageHeader>
      <PageBody>
        <div className="border border-slate-200 bg-white rounded-sm overflow-hidden">
          <table className="erp-table" data-testid="users-grid">
            <thead className="bg-slate-100">
              <tr>{visibleCols.map((c) => <th key={c.k}>{c.label}</th>)}</tr>
            </thead>
            <tbody>
              {pag.pageRows.map((u) => (
                <tr key={u.id} data-testid={T.row("users", u.id)} onClick={() => setEditing({ ...u, teams: u.teams || [], account_ids: u.account_ids || [] })} className="cursor-pointer border-t border-slate-100 hover:bg-indigo-50/40">
                  {visibleCols.map((c) => <React.Fragment key={c.k}>{c.render(u)}</React.Fragment>)}
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={visibleCols.length || 1} className="text-center py-8 text-slate-500 border-t border-slate-100">
                {visibleCols.length === 0 ? "No columns visible for your team." : "No users."}
              </td></tr>}
            </tbody>
          </table>
        </div>
        <div className="mt-2 flex justify-end">
          <PaginationBar page={pag.page} pageSize={pag.pageSize} total={pag.total}
            onPageChange={pag.setPage} onPageSizeChange={pag.setPageSize} testIdPrefix="users" />
        </div>
      </PageBody>

      {editing && (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/30" onClick={() => setEditing(null)}>
          <div className="w-[560px] max-w-full h-full bg-white border-l border-slate-200 flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="h-14 border-b border-slate-200 flex items-center justify-between px-4">
              <div className="font-heading text-[14px] font-semibold">{editing.id ? "Edit User" : "New User"}</div>
              <button onClick={() => setEditing(null)} className="text-slate-500 hover:text-slate-900"><X size={16} /></button>
            </div>
            <div className="flex-1 overflow-auto p-4 space-y-3">
              <Field label="Name"><Input testId={T.formInput("name")} value={editing.name || ""} onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))} /></Field>
              <Field label="Email"><Input testId={T.formInput("email")} type="email" value={editing.email || ""} onChange={(e) => setEditing((s) => ({ ...s, email: e.target.value }))} readOnly={!!editing.id} /></Field>
              <Field label={editing.id ? "New Password (leave blank to keep)" : "Password"}><Input testId={T.formInput("password")} type="password" value={editing.password || ""} onChange={(e) => setEditing((s) => ({ ...s, password: e.target.value }))} /></Field>
              <Field label="Role">
                <Select testId={T.formInput("role")} options={["admin", "user"]} value={editing.role || "user"} onChange={(v) => setEditing((s) => ({ ...s, role: v }))} allowEmpty={false} />
                {editing.role === "admin" && (
                  <div className="mt-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1 inline-flex items-center gap-1">
                    <Shield size={11} /> Admin has full access — team & account restrictions do not apply.
                  </div>
                )}
              </Field>
              {/* Sep 7 2026 — admin sets the UI language. The whole app
                  translates for this user on next page load. Default = English. */}
              <Field label="Language">
                <Select
                  testId="user-preferred-language"
                  options={["en", "zh"]}
                  labels={{ en: "English", zh: "中文 (Chinese)" }}
                  value={editing.preferred_language || "en"}
                  onChange={(v) => setEditing((s) => ({ ...s, preferred_language: v }))}
                  allowEmpty={false}
                />
                <div className="mt-1 text-[11px] text-slate-500" data-notranslate>
                  The user's entire UI displays in the selected language. Takes effect on next login or page refresh.
                </div>
              </Field>
              <Field label={<span className="inline-flex items-center gap-1"><UsersRound size={12} /> Teams <span className="text-slate-400 font-normal">({(editing.teams || []).length})</span></span>}>
                {editing.role !== "admin" && (editing.teams || []).length === 0 && (
                  <div className="mb-2 flex items-start gap-2 rounded-sm border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-900" data-testid="user-no-team-warning">
                    <Shield size={12} className="mt-0.5 shrink-0" />
                    <span><b>Required:</b> pick at least one team below. Users without a team can log in but will land on empty screens with no data.</span>
                  </div>
                )}
                <div className="flex flex-wrap gap-1.5">
                  {teams.map((t) => {
                    const on = (editing.teams || []).includes(t.name);
                    return (
                      <button key={t.id} type="button" onClick={() => toggleArr("teams", t.name)} data-testid={`user-team-${t.name}`} className={`h-7 px-2 text-[11.5px] border rounded-sm ${on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
                        {t.name}
                      </button>
                    );
                  })}
                  {!teams.length && <span className="text-[11.5px] text-slate-500">No teams defined.</span>}
                </div>
              </Field>
              <Field label={
                <span className="inline-flex items-center gap-1">
                  <Building2 size={12} /> Account Allocation
                  <span className="text-slate-400 font-normal">({editingAccountIds.length}/{accounts.length})</span>
                </span>
              }>
                <div className="text-[11px] text-slate-500 mb-1.5">
                  {editing.role === "admin"
                    ? <span className="text-amber-800">Admin ignores this — they see every account.</span>
                    : "Non-admin users only see Bank / Orders / etc. for accounts checked below."}
                </div>
                {accounts.length > 3 && (
                  <div className="mb-2 flex items-center gap-2">
                    <button
                      onClick={() => setEditing((s) => ({ ...s, account_ids: accounts.map((a) => a.account_id) }))}
                      data-testid="user-accounts-select-all"
                      className="text-[11px] text-blue-600 hover:underline"
                    >Select all</button>
                    <button
                      onClick={() => setEditing((s) => ({ ...s, account_ids: [] }))}
                      data-testid="user-accounts-clear"
                      className="text-[11px] text-slate-500 hover:underline"
                    >Clear</button>
                  </div>
                )}
                <div className="flex flex-wrap gap-1.5">
                  {accounts.map((a) => {
                    const on = editingAccountIds.includes(a.account_id);
                    return (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => toggleArr("account_ids", a.account_id)}
                        data-testid={`user-account-${a.account_id}`}
                        title={`${a.company_name || a.marketplace} · ${a.email || ""}`}
                        className={`h-8 px-2 text-[11.5px] font-mono border rounded-sm inline-flex items-center gap-1.5 ${on ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}
                      >
                        <span className="font-semibold">{a.account_id}</span>
                        <span className={`text-[10px] ${on ? "text-indigo-100" : "text-slate-400"}`}>{a.marketplace}</span>
                      </button>
                    );
                  })}
                  {!accounts.length && <span className="text-[11.5px] text-slate-500">Create accounts first (Accounts module).</span>}
                </div>
                {/* Feb 26 2026 — override procurement account scoping so this
                    single user can see every procurement row regardless of
                    their allocated accounts. Admins always see everything. */}
                {editing.role !== "admin" && (
                  <label className="mt-3 inline-flex items-center gap-2 text-[11.5px] py-1 px-2 border border-slate-200 rounded-sm bg-slate-50" data-testid="user-full-procurement-access-row">
                    <input
                      type="checkbox"
                      data-testid="user-full-procurement-access"
                      checked={!!editing.full_procurement_access}
                      onChange={(e) => setEditing((s) => ({ ...s, full_procurement_access: e.target.checked }))}
                    />
                    <span>Full procurement access — see all procurement rows (bypass account scoping)</span>
                  </label>
                )}
              </Field>

              {/* Mar 1 2026 — Warehouse Allocation. Sibling to Account
                  Allocation. Empty = no restriction (backwards-compat).
                  Non-empty = the effective account scope narrows to accounts
                  under these warehouses at auth time. Admins bypass. */}
              <Field label={
                <span className="inline-flex items-center gap-1">
                  <Building2 size={12} /> Warehouse Allocation
                  <span className="text-slate-400 font-normal">({(editing.warehouse_names || []).length}/{warehouses.length})</span>
                </span>
              }>
                <div className="text-[11px] text-slate-500 mb-1.5">
                  {editing.role === "admin"
                    ? <span className="text-amber-800">Admin ignores this — they see every warehouse.</span>
                    : "Non-admin users only see rows whose account is registered under one of these warehouses. Leave empty for no warehouse restriction."}
                </div>
                {editing.role !== "admin" && warehouses.length > 3 && (
                  <div className="mb-2 flex items-center gap-2">
                    <button
                      onClick={() => setEditing((s) => ({ ...s, warehouse_names: warehouses.map((w) => w.name).filter(Boolean) }))}
                      data-testid="user-warehouses-select-all"
                      className="text-[11px] text-blue-600 hover:underline"
                    >Select all</button>
                    <button
                      onClick={() => setEditing((s) => ({ ...s, warehouse_names: [] }))}
                      data-testid="user-warehouses-clear"
                      className="text-[11px] text-slate-500 hover:underline"
                    >Clear</button>
                  </div>
                )}
                <div className="flex flex-wrap gap-1.5">
                  {warehouses.map((w) => {
                    const on = (editing.warehouse_names || []).includes(w.name);
                    return (
                      <button
                        key={w.id || w.name}
                        type="button"
                        onClick={() => toggleArr("warehouse_names", w.name)}
                        data-testid={`user-warehouse-${w.name}`}
                        className={`h-8 px-2 text-[11.5px] font-mono border rounded-sm inline-flex items-center gap-1.5 ${on ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}
                      >
                        <span className="font-semibold">{w.name}</span>
                      </button>
                    );
                  })}
                  {!warehouses.length && <span className="text-[11.5px] text-slate-500">Create warehouses first (Warehouse module).</span>}
                </div>
              </Field>

              {/* Mar 1 2026 — Feature Permissions. Explicit false = the
                  corresponding button is fully hidden for that user. Absent =
                  allowed (backwards-compat). Admins bypass everywhere. */}
              <Field label={<span className="inline-flex items-center gap-1"><Shield size={12} /> Feature Permissions</span>}>
                <div className="text-[11px] text-slate-500 mb-2">
                  {editing.role === "admin"
                    ? <span className="text-amber-800">Admin ignores this — every feature button is visible.</span>
                    : "Uncheck to hide the corresponding button from this user's toolbar."}
                </div>
                {editing.role !== "admin" && (() => {
                  const fp = editing.feature_permissions || {};
                  // Mar 3 2026 — most toggles default to ON (absent = allowed).
                  // Opt-in toggles listed in OPT_IN default to OFF (absent = denied).
                  const OPT_IN = new Set(["simplified_procurement"]);
                  const flagOn = (k) => OPT_IN.has(k) ? fp[k] === true : fp[k] !== false;
                  const setFlag = (k, val) => setEditing((s) => ({
                    ...s,
                    feature_permissions: { ...(s.feature_permissions || {}), [k]: val },
                  }));
                  const TOGGLES = [
                    ["print_labels",     "Print Labels",     "Batch-print packing slips for selected orders."],
                    ["download_template", "Download Template", "Empty CSV with every field for bulk paste."],
                    ["admin_backfills",  "Admin Backfills",  "Backfill / reclassify / danger-zone tools."],
                    // Mar 3 2026 — Simplified Procurement mode. Opt-in: hides
                    // costing/computed fields and lets the user type transfer
                    // price directly (form + import).
                    ["simplified_procurement", "Simplified Procurement Access",
                      "Only Product, Qty, Account, Transfer Price. system_sku optional. Direct transfer_price on the form and in the CSV import."],
                  ];
                  return (
                    <div className="grid grid-cols-1 gap-1.5">
                      {TOGGLES.map(([key, label, hint]) => (
                        <label key={key} className="inline-flex items-start gap-2 text-[11.5px] py-1.5 px-2 border border-slate-200 rounded-sm bg-white" data-testid={`user-feature-${key}-row`}>
                          <input
                            type="checkbox"
                            data-testid={`user-feature-${key}`}
                            checked={flagOn(key)}
                            onChange={(e) => setFlag(key, e.target.checked)}
                            className="mt-0.5"
                          />
                          <span>
                            <span className="block font-semibold text-slate-800">{label}</span>
                            <span className="block text-[10.5px] text-slate-500">{hint}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  );
                })()}
              </Field>

              {/* --- Access-control section (Feb 2026) --- */}
              <div className="pt-3 border-t border-slate-100">
                <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Access Controls</div>
                <div className="text-[11px] text-slate-500 mb-3">
                  Leave any field blank to inherit from the user's team(s). Admin role bypasses all restrictions.
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Login window START (IST, HH:MM)">
                    <Input testId={T.formInput("login_start_time")} placeholder="e.g. 09:00" value={editing.login_start_time || ""}
                           onChange={(e) => setEditing((s) => ({ ...s, login_start_time: e.target.value }))} />
                  </Field>
                  <Field label="Login window END (IST, HH:MM)">
                    <Input testId={T.formInput("login_end_time")} placeholder="e.g. 18:00" value={editing.login_end_time || ""}
                           onChange={(e) => setEditing((s) => ({ ...s, login_end_time: e.target.value }))} />
                  </Field>
                  <Field label="Max parallel sessions" span={2}>
                    <Input testId={T.formInput("max_parallel_sessions")} type="number" min="0" placeholder="0 = unlimited"
                           value={editing.max_parallel_sessions ?? ""}
                           onChange={(e) => setEditing((s) => ({ ...s, max_parallel_sessions: e.target.value === "" ? null : Number(e.target.value) }))} />
                  </Field>
                </div>

                <div className="mt-3">
                  <div className="field-label mb-1.5">Enabled Imports</div>
                  <div className="text-[11px] text-slate-500 mb-2">Uncheck any import type to block this user from running it. Blank = inherit from team.</div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {[
                      // Feb 26 2026 — same expanded list as Teams so admins
                      // can override any import type at the user level.
                      ["orders", "Marketplace Orders"],
                      ["sellerflex-orders", "Sellerflex Orders"],
                      ["flipkart-orders", "Flipkart Orders"],
                      ["marketplace-identifier", "Marketplace Identifier"],
                      ["shipping-update", "Shipping Update"],
                      ["sellerflex-shipping-update", "Sellerflex Shipping Update"],
                      ["order-status-update", "Order Status Update"],
                      ["procurement", "Procurement"],
                      ["accounts", "Accounts"],
                      ["mapping", "Mapping"],
                      ["inventory", "Inventory"],
                      ["warehouse", "Warehouse"],
                      ["customers", "Customers"],
                      ["bank", "Bank Transfers"],
                      ["catalog", "Catalog"],
                      ["asin_images", "ASIN Images"],
                      ["inbound", "Inbound"],
                    ].map(([key, label]) => {
                      const perms = editing.import_permissions || {};
                      const has = key in perms;
                      return (
                        <label key={key} className="inline-flex items-center gap-2 text-[11.5px] py-0.5">
                          <input
                            type="checkbox"
                            data-testid={`user-import-${key}`}
                            checked={perms[key] === true}
                            ref={(el) => { if (el) el.indeterminate = !has; }}
                            onChange={(e) => setEditing((s) => {
                              const p = { ...(s.import_permissions || {}) };
                              if (!has) p[key] = e.target.checked;
                              else if (p[key]) p[key] = false;
                              else delete p[key];  // 3-state toggle: on → off → inherit
                              return { ...s, import_permissions: p };
                            })}
                          />
                          <span className="truncate">{label}</span>
                          {!has && <span className="text-[10px] text-slate-400 font-mono">inherit</span>}
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Feb 26 2026 — per-user extra module grants.
                    UNION with team perms: any View / Edit ticked here is
                    added on top of what the team already grants. Use it to
                    give one user access to a module their team doesn't have.
                    (Field-level overrides stay on the Teams page.) */}
                <div className="mt-4">
                  <div className="field-label mb-1.5">Extra Module Access (grants on top of team)</div>
                  <div className="text-[11px] text-slate-500 mb-2">Tick to grant this user extra View/Edit access. Untick = inherit from team.</div>
                  <div className="border border-slate-200 rounded-sm max-h-64 overflow-auto">
                    <table className="erp-table text-[11.5px]">
                      <thead>
                        <tr>
                          <th>Module</th>
                          <th className="text-center w-16">View</th>
                          <th className="text-center w-16">Edit</th>
                          <th className="text-center w-16">Delete</th>
                          <th className="text-center w-20" title="Grants the Bulk Update bar for that module">Bulk Edit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(modules || []).map((m) => {
                          const mp = editing.permissions?.[m] || {};
                          return (
                            <tr key={m}>
                              <td className="capitalize">{m.replaceAll("_", " ")}</td>
                              <td className="text-center">
                                <input type="checkbox" data-testid={`user-perm-${m}-view`}
                                       checked={!!mp.view}
                                       onChange={(e) => setEditing((s) => {
                                         const p = { ...(s.permissions || {}) };
                                         const cur = { ...(p[m] || {}) };
                                         if (e.target.checked) cur.view = true;
                                         else { delete cur.view; delete cur.edit; delete cur.delete; delete cur.bulk_edit; }
                                         if (cur.view || cur.edit || cur.delete || cur.bulk_edit) p[m] = cur; else delete p[m];
                                         return { ...s, permissions: p };
                                       })} />
                              </td>
                              <td className="text-center">
                                <input type="checkbox" data-testid={`user-perm-${m}-edit`}
                                       checked={!!mp.edit}
                                       onChange={(e) => setEditing((s) => {
                                         const p = { ...(s.permissions || {}) };
                                         const cur = { ...(p[m] || {}) };
                                         if (e.target.checked) { cur.edit = true; cur.view = true; }
                                         else { delete cur.edit; delete cur.delete; delete cur.bulk_edit; }
                                         if (cur.view || cur.edit || cur.delete || cur.bulk_edit) p[m] = cur; else delete p[m];
                                         return { ...s, permissions: p };
                                       })} />
                              </td>
                              <td className="text-center">
                                <input type="checkbox" data-testid={`user-perm-${m}-delete`}
                                       checked={!!mp.delete}
                                       onChange={(e) => setEditing((s) => {
                                         const p = { ...(s.permissions || {}) };
                                         const cur = { ...(p[m] || {}) };
                                         if (e.target.checked) { cur.delete = true; cur.edit = true; cur.view = true; }
                                         else delete cur.delete;
                                         if (cur.view || cur.edit || cur.delete || cur.bulk_edit) p[m] = cur; else delete p[m];
                                         return { ...s, permissions: p };
                                       })} />
                              </td>
                              <td className="text-center">
                                <input type="checkbox" data-testid={`user-perm-${m}-bulk_edit`}
                                       checked={!!mp.bulk_edit}
                                       onChange={(e) => setEditing((s) => {
                                         const p = { ...(s.permissions || {}) };
                                         const cur = { ...(p[m] || {}) };
                                         if (e.target.checked) { cur.bulk_edit = true; cur.edit = true; cur.view = true; }
                                         else delete cur.bulk_edit;
                                         if (cur.view || cur.edit || cur.delete || cur.bulk_edit) p[m] = cur; else delete p[m];
                                         return { ...s, permissions: p };
                                       })} />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* ---- Active Sessions manager (visible only after user is saved) ---- */}
            {editing.id && (
              <div className="border-t border-slate-200 bg-slate-50/60 px-4 py-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-600 font-semibold">
                    <Monitor size={12} /> Active Sessions {sessions.length > 0 && <span className="text-slate-400 font-mono">({sessions.length})</span>}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      className="text-[11px] text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
                      onClick={() => loadSessions(editing.id)}
                      data-testid="sessions-refresh"
                    ><RefreshCw size={11} /> Refresh</button>
                    {sessions.length > 0 && (
                      <button
                        className="ml-2 text-[11px] text-red-700 hover:underline"
                        onClick={revokeAll}
                        data-testid="sessions-revoke-all"
                      >Revoke all</button>
                    )}
                  </div>
                </div>
                {sessionsLoading ? (
                  <div className="text-[11px] text-slate-500">Loading…</div>
                ) : sessions.length === 0 ? (
                  <div className="text-[11px] text-slate-500">No active sessions.</div>
                ) : (
                  <div className="max-h-40 overflow-auto rounded-sm border border-slate-200 bg-white">
                    <table className="erp-table">
                      <thead>
                        <tr><th>Signed in</th><th>IP</th><th>Browser</th><th className="w-16"></th></tr>
                      </thead>
                      <tbody>
                        {sessions.map((s) => (
                          <tr key={s.id}>
                            <td className="font-mono">{(s.created_at || "").slice(0, 19).replace("T", " ")}</td>
                            <td className="font-mono">{s.ip || "—"}</td>
                            <td className="truncate max-w-[200px] text-[11px]" title={s.user_agent}>{(s.user_agent || "—").split(" ").slice(-2).join(" ")}</td>
                            <td>
                              <button
                                data-testid={`sessions-revoke-${s.id}`}
                                onClick={() => revokeSession(s.id)}
                                className="text-red-700 hover:underline text-[11px]"
                              >Revoke</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            <div className="h-14 border-t border-slate-200 flex items-center justify-between px-4">
              {editing.id ? <Button variant="danger" onClick={del} testId={T.deleteBtn}><Trash2 size={13} /> Delete</Button> : <span />}
              <div className="flex gap-2">
                <Button onClick={() => setEditing(null)} testId={T.cancelBtn}>Cancel</Button>
                <Button variant="primary" onClick={save} testId={T.saveBtn}>Save</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
